import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '@dental-manager/shared';

/**
 * Devise et locale courantes.
 *
 * État au niveau du module — volontairement : les ~40 appels existants à
 * formatCurrency(value) restent inchangés. La devise ne change qu'au login /
 * changement de cabinet (re-render complet), la locale au changement de langue
 * (i18next re-rend les composants qui utilisent t()).
 */
let currentCurrency: string = DEFAULT_CURRENCY;
let currentLocale: string = DEFAULT_LOCALE;

/** Appelé par AuthContext au login, getMe, switchAccount et changement de langue. */
export function setCurrencyContext(currency?: string | null, locale?: string | null): void {
  if (currency) currentCurrency = currency;
  if (locale) currentLocale = locale;
}

export function getCurrency(): string {
  return currentCurrency;
}

/**
 * Symbole de la devise courante (€, RON, CHF…) — pour les libellés du type
 * "Valeur (€)" et les axes de graphiques.
 */
export function currencySymbol(): string {
  const parts = new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currentCurrency,
  }).formatToParts(0);
  return parts.find(p => p.type === 'currency')?.value ?? currentCurrency;
}

/**
 * Format a number as currency in the cabinet's currency and the UI locale
 * (e.g., 1 000,00 € en fr-FR, 1.000,00 RON en ro-RO)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currentCurrency,
  }).format(value);
}

/**
 * Format a number as currency without decimals (e.g., 1 000 €) — pour les KPI
 */
export function formatCurrencyCompact(value: number): string {
  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currentCurrency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a number with the UI locale thousand separators (e.g., 1 500 → "1 500")
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat(currentLocale).format(value);
}

/**
 * Format product display name with optional variant label
 * @param productName - The product name
 * @param variantLabel - The variant label (null/empty for unique products)
 * @param separator - Separator between name and label (default: " — ")
 * @returns Formatted display string
 *
 * @example
 * formatProductVariant("Gants Latex", "M") // "Gants Latex — M"
 * formatProductVariant("Gants Latex M", null) // "Gants Latex M"
 * formatProductVariant("Gants Latex M", "") // "Gants Latex M"
 */
export function formatProductVariant(
  productName: string,
  variantLabel?: string | null,
  separator: string = ' — '
): string {
  if (!variantLabel || variantLabel.trim() === '') {
    return productName;
  }
  return `${productName}${separator}${variantLabel}`;
}
